import { TMY_DATASET_SCHEMA_VERSION, type TmyDataset, type TmyHour, type TmyHottestHoursSummary } from '../../types/tmy';
import { hashEconomicModelInput, roundEconomic } from './modelUtils';
import { tmyDatasetValidationErrors } from './adiabaticWaterScreening';

/**
 * Share of hours in ASHRAE's 0.4 % annual design level: 35 of 8,760.
 * ASHRAE Handbook—Fundamentals Ch. 14 pairs each dry-bulb design level with
 * the mean wet bulb coincident with it (MCWB).
 */
export const TMY_HOTTEST_HOURS_FRACTION = 0.004;

/**
 * Mean wet bulb coincident with the hottest 0.4 % of hours of a validated
 * 8,760-hour TMY year, °C, for the adiabatic temperature-headroom screen.
 *
 * Replaces the SED screening wet bulb in `buildDryCoolingDesignScreen` when
 * hourly TMY is available (owner decision, 28 September 2026: mean of the
 * hottest hours). The SED values are a monthly mean (Open-Meteo) or an
 * assumed-RH estimate (NOAA normals), so neither coincides with the hot hours
 * that set adiabatic headroom. Each TMY hour carries an ASHRAE thermodynamic
 * wet bulb computed from that hour's dry bulb, RH and pressure, so hot-hour
 * pairs are genuinely coincident. The mean, not the maximum, keeps a single
 * humid hour from setting the value.
 *
 * This remains a screening value from one typical year. It is not an ASHRAE
 * design percentile, which uses a multi-year hourly record. Evaporative
 * equipment can also be limited by high-wet-bulb hours that are not the
 * hottest dry-bulb hours.
 *
 * The server can freeze an auxiliary validated summary in the SED. Read-time
 * dry-cooling screening uses it without adding ambient data to the persisted
 * water-model request or performing another weather lookup.
 *
 * Callers pass hours only after `calculateAdiabaticWaterScreening` has
 * validated the TMY year. Hours without finite dry and wet bulbs are ignored,
 * and dry-bulb ties keep input order (stable sort), so the result is
 * deterministic. Returns null when no usable hour exists; the caller then
 * falls back to the SED screening wet bulb.
 */
export function tmyCoincidentWetBulbC(hours: readonly TmyHour[]): number | null {
  const usable = hours.filter(hour => Number.isFinite(hour.dryBulbC) && Number.isFinite(hour.wetBulbC));
  if (usable.length === 0) return null;
  // Round, not ceil: 8,760 × 0.004 = 35.04 must give ASHRAE's 35 hours.
  const count = Math.max(1, Math.round(usable.length * TMY_HOTTEST_HOURS_FRACTION));
  const hottest = [...usable].sort((a, b) => b.dryBulbC - a.dryBulbC).slice(0, count);
  const meanWetBulbC = hottest.reduce((sum, hour) => sum + hour.wetBulbC, 0) / hottest.length;
  return roundEconomic(meanWetBulbC, 1);
}

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const roundedCoordinate = (value: number) => Math.round(value * 100) / 100;
const SOURCES = new Set(['nrel-psm3', 'pvgis', 'ashrae-iwec2']);

/** Summarize only the complete validated dataset already loaded by the server. */
export function summarizeTmyHottestHours(
  tmy: TmyDataset,
  site: { id: string; location: { lat: number; lng: number } },
): TmyHottestHoursSummary | null {
  if (!site.id || !finite(site.location.lat) || !finite(site.location.lng)
    || Math.abs(site.location.lat) > 90 || Math.abs(site.location.lng) > 180
    || tmyDatasetValidationErrors(tmy).length || !SOURCES.has(tmy.source)
    || !Number.isFinite(Date.parse(tmy.fetchedAt))
    || tmy.latitudeRounded !== roundedCoordinate(site.location.lat)
    || tmy.longitudeRounded !== roundedCoordinate(site.location.lng)) return null;
  const hottest = [...tmy.hours].sort((a, b) => b.dryBulbC - a.dryBulbC).slice(0, 35);
  const basis: Omit<TmyHottestHoursSummary, 'summaryHash'> = {
    summaryVersion: 'tmy-hottest-hours-v1', siteId: site.id,
    datasetSchemaVersion: TMY_DATASET_SCHEMA_VERSION,
    datasetHash: hashEconomicModelInput(tmy), source: tmy.source,
    datasetYear: tmy.datasetYear, fetchedAt: tmy.fetchedAt,
    latitudeRounded: tmy.latitudeRounded, longitudeRounded: tmy.longitudeRounded,
    ...(tmy.sourceVersion ? { sourceVersion: tmy.sourceVersion } : {}),
    ...(tmy.datasetName ? { datasetName: tmy.datasetName } : {}),
    ...(tmy.gridLatitude != null ? { gridLatitude: tmy.gridLatitude } : {}),
    ...(tmy.gridLongitude != null ? { gridLongitude: tmy.gridLongitude } : {}),
    ...(tmy.locationId ? { locationId: tmy.locationId } : {}),
    datasetHourCount: 8760, hourCount: 35,
    meanDryBulbC: roundEconomic(hottest.reduce((sum, hour) => sum + hour.dryBulbC, 0) / 35, 1),
    meanWetBulbC: roundEconomic(hottest.reduce((sum, hour) => sum + hour.wetBulbC, 0) / 35, 1),
  };
  const result = { ...basis, summaryHash: hashEconomicModelInput(basis) };
  return readTmyHottestHoursSummary(result, site) ? result : null;
}

/** Runtime shape/content and optional canonical site fence; never reconstruct hours. */
export function readTmyHottestHoursSummary(
  value: unknown,
  site?: { id?: string; location: { lat: number; lng: number } },
): TmyHottestHoursSummary | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const keys = new Set(['summaryVersion', 'siteId', 'datasetSchemaVersion', 'datasetHash', 'summaryHash', 'source', 'sourceVersion', 'datasetName', 'datasetYear', 'latitudeRounded', 'longitudeRounded', 'gridLatitude', 'gridLongitude', 'locationId', 'fetchedAt', 'datasetHourCount', 'hourCount', 'meanDryBulbC', 'meanWetBulbC']);
  if (Object.keys(value).some(key => !keys.has(key))) return null;
  const summary = value as TmyHottestHoursSummary;
  if (summary.summaryVersion !== 'tmy-hottest-hours-v1'
    || summary.datasetSchemaVersion !== TMY_DATASET_SCHEMA_VERSION
    || typeof summary.siteId !== 'string' || !summary.siteId.trim()
    || !SOURCES.has(summary.source)
    || typeof summary.datasetHash !== 'string' || !/^[a-f0-9]{64}$/.test(summary.datasetHash)
    || typeof summary.summaryHash !== 'string' || !/^[a-f0-9]{64}$/.test(summary.summaryHash)
    || summary.datasetHourCount !== 8760 || summary.hourCount !== 35
    || (summary.datasetYear !== null && (!Number.isInteger(summary.datasetYear) || summary.datasetYear < 1900 || summary.datasetYear > 9999))
    || typeof summary.fetchedAt !== 'string' || !Number.isFinite(Date.parse(summary.fetchedAt))
    || !finite(summary.latitudeRounded) || Math.abs(summary.latitudeRounded) > 90
    || !finite(summary.longitudeRounded) || Math.abs(summary.longitudeRounded) > 180
    || !finite(summary.meanDryBulbC) || summary.meanDryBulbC < -100 || summary.meanDryBulbC > 70
    || !finite(summary.meanWetBulbC) || summary.meanWetBulbC < -100 || summary.meanWetBulbC > summary.meanDryBulbC
    || (summary.gridLatitude != null && (!finite(summary.gridLatitude) || Math.abs(summary.gridLatitude) > 90))
    || (summary.gridLongitude != null && (!finite(summary.gridLongitude) || Math.abs(summary.gridLongitude) > 180))
    || [summary.sourceVersion, summary.datasetName, summary.locationId].some(text => text != null && (typeof text !== 'string' || !text.trim()))) return null;
  if (site && ((site.id != null && site.id !== summary.siteId)
    || !finite(site.location.lat) || !finite(site.location.lng)
    || roundedCoordinate(site.location.lat) !== summary.latitudeRounded
    || roundedCoordinate(site.location.lng) !== summary.longitudeRounded)) return null;
  const { summaryHash, ...basis } = summary;
  try { return hashEconomicModelInput(basis) === summaryHash ? summary : null; }
  catch { return null; }
}

/** Public source locator, without any provider credentials/request query. */
export function tmyHottestHoursSource(summary: TmyHottestHoursSummary): { publisher: string; url: string } {
  if (summary.source === 'nrel-psm3') return { publisher: 'NREL NSRDB', url: 'https://developer.nlr.gov/docs/solar/nsrdb/' };
  if (summary.source === 'pvgis') return { publisher: 'European Commission JRC PVGIS', url: 'https://re.jrc.ec.europa.eu/pvg_tools/en/' };
  return { publisher: 'ASHRAE IWEC2', url: 'https://www.ashrae.org/technical-resources/bookstore/ashrae-international-weather-for-energy-calculations-2-0-iwec2' };
}

export function tmyHottestHoursBasisText(summary: TmyHottestHoursSummary): string {
  return `Validated TMY hottest ${summary.hourCount} of ${summary.datasetHourCount} hours: paired mean dry bulb ${summary.meanDryBulbC}°C and wet bulb ${summary.meanWetBulbC}°C; `
    + `${summary.source}${summary.sourceVersion ? ` ${summary.sourceVersion}` : ''}${summary.datasetName ? ` (${summary.datasetName})` : ''}; `
    + `year ${summary.datasetYear ?? 'composite'}; requested coordinates ${summary.latitudeRounded}, ${summary.longitudeRounded}`
    + `${summary.gridLatitude != null && summary.gridLongitude != null ? `; source-grid coordinates ${summary.gridLatitude}, ${summary.gridLongitude}` : ''}; `
    + `retrieved ${summary.fetchedAt}; dataset SHA-256 ${summary.datasetHash}; ${summary.summaryVersion} summary SHA-256 ${summary.summaryHash}. Screening only, not an ASHRAE design percentile, equipment selection or duty guarantee.`;
}
